import { IPivotItemProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPDefaultButton,
  IPIconButton,
  IPModal,
  IPPivot,
  IPPivotItem,
  IPPrimaryButton,
  IPSeparator,
  IPText,
  IPTooltipHost,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ActivityTrail from 'components/Activity/ActivityTrail';
import AuditTrail from 'components/Audit/AuditTrail';
import { IEvidenceTaskLinkItem } from 'components/AuditCriteria/EvidenceTaskLinks';
import { DialogOk } from 'components/Dialogs/DialogOk';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import TaskLinkList from 'components/Links/LinkLists/TaskLinkList';
import OverlayLoader from 'components/Loading/OverlayLoader';
import { ErrorMessage } from 'components/Notification/ErrorMessage';
import {
  cancelIcon,
  globalStackStylesHeight100,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import Joi from 'joi';
import { AuditEntityKey, auditCriterionModeOneTime, getAuditEntityKeysOfTask } from 'models/auditCriteria';
import { AuditStatus } from 'models/internalAudit';
import { auditConclusionMinLength, AuditResultBlock } from 'models/internalAuditExecution';
import ResourceLink from 'models/resourceLink';
import Task, { TaskStateId, TaskTypes } from 'models/tasks/task';
import TaskType from 'models/tasks/taskType';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetEvidenceTaskBindings } from 'services/Api/auditCriteriaService';
import { apiGetSingleTask } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { newGuid } from 'utils/guid';
import { TaskContext } from '../../Context/TaskContext';
import TaskTypeForm from '../../Forms/TaskTypeForm';
import SingleTask, { ISingleTaskTypeProps } from '../../SingleTask';
import { canUpdateTaskField, TaskFieldTypes } from '../../TaskAuthHelper';
import TaskTopTasksBar from '../../TaskTopTasksBar';
import { getTaskViewModalStackStyles, getTaskViewModalStyles } from '../../View/TaskRenderHelpers';
import { TaskDetailOwner } from '../Details/TaskDetailOwner';
import { AuditExecutionTaskDetail } from './AuditExecutionTaskDetail';

interface IAuditExecutionTaskProps extends ISingleTaskTypeProps {
  onChangeCompletionDate: (completionDate: Date) => void;
  onReopenTask: () => Promise<void>;
}

// The auditee's view of one piece of audit work: the scope item that must be audited, the
// instruction and the result field. The task itself is an ordinary task - what makes it audit work
// is its link to the audit - so everything else here is the standard task chrome.
// Three commands are suppressed by not handing the bar their callbacks, the way an approval task
// does it: save-as-template, because execution tasks must not seed the generic template library, and
// copy + remove, because the audit owns its work. A copy would be an orphan that belongs to no audit,
// and removing is Unassign on the Execute tab, which first asks what happens to the findings raised
// from this task.
const AuditExecutionTask = (props: IAuditExecutionTaskProps) => {
  //'forms' is in the list because the form-mode tooltip below resolves in it: a namespace this
  //component reads but does not declare is loaded lazily and renders as a raw key
  const { t } = useTranslation(['audit', 'task', 'control', 'forms', 'translation', 'dateTimeComponent', 'dashboard']);
  const appContext = useContext(AppContext);
  const [err, setErr] = useState<Joi.ValidationError | undefined>(undefined);
  const [selectedPivot, setSelectedPivot] = useState<string>(props.selectedPivot ?? 'TaskType');
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const [resultBlock, setResultBlock] = useState<AuditResultBlock>(AuditResultBlock.None);
  const [showResultRequiredDialog, setShowResultRequiredDialog] = useState<boolean>(false);
  const [auditStatus, setAuditStatus] = useState<AuditStatus | undefined>(undefined);
  const [resultDirty, setResultDirty] = useState<boolean>(false);
  //a ref, not state: onBeforeSave is registered once, so it must read the current flush
  const saveResultRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const [evidenceTasksByCriterionKey, setEvidenceTasksByCriterionKey] = useState<Map<string, IEvidenceTaskLinkItem[]>>(
    new Map(),
  );
  const [evidenceTask, setEvidenceTask] = useState<Task | undefined>(undefined);
  //the audited entities under the names the auditor knows them by, read by the detail pane: they
  //head the questions of each entity on the form
  const [entityNamesByKey, setEntityNamesByKey] = useState<Map<string, string>>(new Map());
  //the auditor took over an AI result: the form fills the answers of that item's questions with the
  //AI's, the detail pane the outcome and the conclusion. A new request is what applies it
  const [takeOverAiAnswers, setTakeOverAiAnswers] = useState<
    { requestId: string; entity: AuditEntityKey } | undefined
  >(undefined);

  //the task carries a form when a form type is assigned to it and the tenant has the form feature
  const hasForm = props.hasFormFeature && !!props.task.taskTypeId && props.task.taskTypeId > 0;

  //criteria are managed on the procedure and per framework, both behind the Audit feature, so only
  //a user who holds it is told where to go
  const canManageCriteria = hasUserFeature(appContext, FeatureTypes.Audit);

  // Results freeze at Completed, so from there on reopening the task cannot unlock them any more:
  // offering Reopen would be a dead end. The audit itself has to be moved back first.
  const auditFrozen = auditStatus !== undefined && auditStatus >= AuditStatus.Completed;

  // Two records, two reasons to freeze, and every tab below answers to both: a completed audit locks
  // its work until it is moved back, a finished task holds what it was closed with until it is
  // reopened. The SAVED state decides: picking another status in the dropdown must not unlock a tab
  // that the next Save could put back. Derived, never state - auditStatus arrives asynchronously.
  const taskFrozen =
    props.orgTask.taskStateId === TaskStateId.Completed || props.orgTask.taskStateId === TaskStateId.Archived;
  const frozen = auditFrozen || taskFrozen;

  useEffect(() => {
    const _localizedFields = {
      assignment: t('task:Assignment.Label'),
      startDateTime: t('task:Start.Label'),
      endDateTime: t('task:End.ErrorMessage'),
      name: t('task:Name.Label'),
      deadline: t('task:Deadline.ErrorMessage'),
    };

    const result = props.task.validate(_localizedFields);
    setErr(result.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task]);

  // The form is the only instrument on an audit execution task: the criteria are projected onto it,
  // so there is no checklist tab left to fall back to. A task without a form still opens on this
  // tab; its body is then the empty state, which says where criteria are managed.
  useEffect(() => {
    if (appContext.isMobileView) {
      setSelectedPivot('TaskDetails');
      props.setFormMode(false);
    } else {
      setSelectedPivot(props.hasFormFeature ? 'TaskType' : 'Context');
    }
    // setFormMode is re-created by the parent on every render: listing it here would re-run this
    // effect continuously and keep resetting the pivot the user just picked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appContext.isMobileView, props.hasFormFeature]);

  useEffect(() => {
    //Single task requests to show a specific pivot. 'Checklist' is not one of ours - the criteria are
    //questions on the form here - so it is answered with the tab that holds them instead of leaving
    //the auditor on a tab that does not exist
    if (props.selectedPivot === 'Checklist') {
      setSelectedPivot(props.hasFormFeature ? 'TaskType' : 'Context');
    } else if (props.selectedPivot) {
      setSelectedPivot(props.selectedPivot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedPivot]);

  useEffect(() => {
    props.onBeforeSaveCallback(onBeforeSave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The one-time evidence tasks bound to the criteria this task's FORM was projected from, so the
  // auditor can open the task that holds the evidence the criterion asks them to inspect. Keyed by
  // criterion key, which is what the projected form field carries. Best-effort: without them the
  // field simply shows no link. The auditor still judges every criterion - nothing is answered for
  // them.
  useEffect(() => {
    const loadEvidenceTasks = async (entityKeys: AuditEntityKey[]) => {
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const rowsPerEntity = await Promise.all(
          entityKeys.map((key) => apiGetEvidenceTaskBindings(key.entityType, key.entityId, accessToken)),
        );

        const byCriterionKey = new Map<string, IEvidenceTaskLinkItem[]>();
        rowsPerEntity.flat().forEach((row) => {
          if (!row.criterionKey || row.mode !== auditCriterionModeOneTime) return;
          const key = row.criterionKey.toLowerCase();
          const tasks = byCriterionKey.get(key) ?? [];
          //one criterion is projected once across the scope entities, so merge the bindings the same way
          if (!tasks.some((task) => task.taskId === row.taskId)) {
            tasks.push({ taskId: row.taskId, taskName: row.taskName });
          }
          byCriterionKey.set(key, tasks);
        });

        setEvidenceTasksByCriterionKey(byCriterionKey);
      } catch {
        //ignore: the links are extra context, never a reason to fail opening the task
      }
    };

    const entityKeys = getAuditEntityKeysOfTask(props.task);
    if (entityKeys.length > 0) {
      loadEvidenceTasks(entityKeys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task.taskId]);

  const openEvidenceTask = async (taskId: number) => {
    try {
      appContext.showContentLoader();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const tasks = await apiGetSingleTask(taskId, false, accessToken, appContext.globalDataCache);
      if (tasks.tasks.length > 0) {
        setEvidenceTask(tasks.tasks[0]);
      } else {
        appContext.showNotification(t('dashboard:Errors.ItemHasBeenDeleted'), true);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
    }
  };

  //
  // Validate
  //
  const onBeforeSave = async (task: Task): Promise<Task | undefined> => {
    //the audit result is not part of the task: it is written here, so that saving the task stores
    //the outcome and conclusion the auditee typed. Runs after the checks above, so a cancelled save
    //never records a result. A failure throws on purpose: SingleTask reports it and skips the save
    await saveResultRef.current?.();

    return task;
  };

  const validate = (fieldName: string): string | undefined => {
    if (err) {
      const fieldError = err.details.find((e) => {
        return e.context?.key === fieldName || e.message.toLowerCase().includes(fieldName.toLowerCase());
      });

      return fieldError ? fieldError.message : undefined;
    }

    return undefined;
  };

  const hasErrors = (): boolean => {
    return err ? true : false;
  };

  //an edited result counts as a change: it is stored by this Save, so it must be able to enable it
  const cannotSaveTask = (): boolean => {
    return (props.task.taskId !== -1 && props.task.isEqual(props.orgTask) && !resultDirty) || hasErrors();
  };

  const getIsOutlookCreateDisabled = (): boolean => {
    return (
      hasErrors() ||
      props.isActionPending ||
      !props.task.isAssigned() ||
      (props.disallowSchedule ?? false) ||
      (props.disallowSave ?? false) ||
      !canUpdateTaskField(props.task, props.canUpdate, TaskFieldTypes.OutlookRemove, appContext)
    );
  };

  const onCancel = () => {
    if (cannotSaveTask()) {
      props.onCancel();
    } else {
      setShowCancelDialog(true);
    }
  };

  // The AI report document: the server wrote it AND attached it to this task, so it is already
  // stored. It goes into the working copy and into the original alike - putting it only in the
  // working copy would make the task look dirty over a change that is already saved, and leaving
  // it out of both means the next save posts a link list without it, which detaches it again.
  const onReportLinkCreated = (link: ResourceLink) => {
    if (props.links.some((existing) => existing.linkId === link.linkId)) return;

    props.addLinkToTaskState([link], false);
    const newOrgTask = props.orgTask.clone();
    newOrgTask.resourceLinkIds = [...(props.orgTask.resourceLinkIds ?? []), link.linkId];
    props.onUpdateOrgTask(newOrgTask);
  };

  // Suggest conclusion reads the STORED task, so what is typed in this modal has to be on the server
  // before it is asked - the pane must not get a conclusion written over a draft it cannot see. Nothing
  // dirty means nothing to flush; the save carries the result with it through onBeforeSave, exactly as
  // the auditee's compact pane does on its autosave. false is reported by the pane and nothing is asked.
  const flushDraft = async (): Promise<boolean> => {
    if (props.task.isEqual(props.orgTask) && !resultDirty) return true;
    if (!props.saveWithoutClose) return false;

    return props.saveWithoutClose();
  };

  const getTaskDetails = () => {
    return (
      <AuditExecutionTaskDetail
        task={props.task}
        orgTask={props.orgTask}
        getErrorMessage={validate}
        isActionPending={props.isActionPending}
        windowLevel={props.windowLevel}
        navigateExternal={props.navigateExternal}
        canUpdate={props.canUpdate}
        onResultBlockChange={setResultBlock}
        onResultDirtyChange={setResultDirty}
        onRegisterSaveResult={(saveResult) => {
          saveResultRef.current = saveResult;
        }}
        onAuditStatusChange={setAuditStatus}
        onEntityNamesChange={setEntityNamesByKey}
        onFlushDraft={flushDraft}
        onReportLinkCreated={onReportLinkCreated}
        onOpenEvidenceTask={(taskId) => void openEvidenceTask(taskId)}
        onTakeOverAiAnswers={(entity) => setTakeOverAiAnswers({ requestId: newGuid(), entity })}
      />
    );
  };

  const onClickFinish = () => {
    //the result of an execution task is the audit's evidence: the save below stores it, but there
    //must be one, otherwise the audit closes with an unassessed scope row
    if (resultBlock !== AuditResultBlock.None) {
      setShowResultRequiredDialog(true);

      return;
    }

    const newTask = props.task.clone();
    newTask.setComplete();
    props.onUpdateTask(newTask);
    props.onSave(newTask, false);
  };

  const onClickArchive = () => {
    const newTask = props.task.clone();
    newTask.setArchived();
    props.onUpdateTask(newTask);
    props.onSave(newTask, false);
  };

  //an auditee who finished too early must be able to correct the result: reopening the task unlocks
  //the result editor again, as long as the audit itself is still open
  const onClickReopen = () => {
    props.onReopenTask();
  };

  //the auditee must be told what is missing: the outcome or the conclusion text
  const getResultRequiredText = (): string => {
    switch (resultBlock) {
      case AuditResultBlock.ShortConclusion:
        return t('audit:Execute.Task.ResultRequired.ShortConclusion', { min: auditConclusionMinLength });
      default:
        return t('audit:Execute.Task.ResultRequired.NoOutcome');
    }
  };

  //
  // Render helpers
  //
  const getOutlookButton = (): JSX.Element | null => {
    if (!props.task.eventId && props.task.taskId !== -1) {
      return (
        <IPStack>
          <IPStack.Item>
            <IPPrimaryButton
              className="isoplanner-task-scheduleinoutlook"
              disabled={getIsOutlookCreateDisabled()}
              onClick={() => {
                props.createInOutlook();
              }}
            >
              {t('task:Outlook.CreateOutlook')}
            </IPPrimaryButton>
          </IPStack.Item>
        </IPStack>
      );
    } else if (props.task.eventId && props.task.taskId !== -1) {
      return (
        <IPStack>
          <IPStack.Item>
            <IPPrimaryButton
              text={t('task:Outlook.RemoveOutlook')}
              disabled={
                hasErrors() ||
                props.isActionPending ||
                !canUpdateTaskField(props.task, props.canUpdate, TaskFieldTypes.OutlookCreate, appContext)
              }
              onClick={() => {
                props.removeFromOutlook();
              }}
            />
          </IPStack.Item>
        </IPStack>
      );
    }

    return null;
  };

  const onRenderTaskTypePivotItem = (
    itemProps?: IPivotItemProps,
    defaultRender?: (itemProps?: IPivotItemProps) => JSX.Element | null,
  ): JSX.Element | null => {
    //itemScope contains true when in compact view
    if (!itemProps || !itemProps.itemKey || !defaultRender) return null;

    //render default for compact mode
    if (itemProps.itemScope === true) {
      return defaultRender(itemProps);
    }

    return (
      <IPStack horizontal tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPText styles={itemProps.itemKey === selectedPivot ? globalTextStylesBold : undefined}>
            {itemProps.headerText}
          </IPText>
        </IPStack.Item>
        {hasForm && (
          <IPStack.Item>
            <IPTooltipHost content={t('forms:View.EnterFormMode')}>
              <IPIconButton iconProps={{ iconName: 'Fullscreen' }} onClick={() => props.setFormMode(true)} />
            </IPTooltipHost>
          </IPStack.Item>
        )}
      </IPStack>
    );
  };

  // Criteria are not managed from an execution task, so the empty form only says what the state is;
  // a user who may manage them is told where. Without questions because none apply under this
  // audit's standards there is nothing to add anywhere, so that state carries no hint.
  const renderEmptyFormState = (noApplicableCriteria?: boolean): JSX.Element => {
    return (
      <IPStack horizontalAlign="center" tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPText>
            {noApplicableCriteria
              ? t('audit:Execute.Task.EmptyForm.NoApplicableCriteria')
              : t('audit:Execute.Task.EmptyForm.TextReadOnly')}
          </IPText>
        </IPStack.Item>
        {!noApplicableCriteria && canManageCriteria && (
          <IPStack.Item>
            <IPText>{t('audit:Execute.Task.EmptyForm.ManageHint')}</IPText>
          </IPStack.Item>
        )}
      </IPStack>
    );
  };

  //
  // Footer
  //
  const footer = (
    <IPStack horizontal={!appContext.isMobileView} verticalAlign="end" tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPStack horizontal tokens={globalStackTokensGapSmall}>
          <IPPrimaryButton
            disabled={cannotSaveTask() || props.isActionPending}
            onClick={async () => {
              props.onSave(props.task, false);
            }}
          >
            {t('translation:General.Button.Save')}
          </IPPrimaryButton>
          <IPDefaultButton
            onClick={() => {
              onCancel();
            }}
          >
            {t('translation:General.Button.Cancel')}
          </IPDefaultButton>
        </IPStack>
      </IPStack.Item>
      <IPStack.Item grow>
        <IPStack horizontal horizontalAlign={appContext.isMobileView ? undefined : 'space-around'}>
          {getOutlookButton()}
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );

  //
  // Main render
  //
  return (
    <IPModal forceFocusInsideTrap={false} isOpen={props.isOpen} styles={getTaskViewModalStyles(props.windowLevel)}>
      {props.isLoading && <OverlayLoader text={t('task:Progress.Save')} />}
      <IPStack
        verticalFill
        tokens={globalStackTokensGapSmall}
        styles={getTaskViewModalStackStyles(appContext.isMobileView, false)}
      >
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center" horizontalAlign="space-between">
            <IPStack.Item>
              <IPStack horizontal={!appContext.isMobileView} verticalAlign="center" tokens={globalStackTokensGapMedium}>
                <IPStack.Item>
                  <IPText variant="xxLarge">{t('task:Title.AuditExecution.Edit')}</IPText>
                </IPStack.Item>
              </IPStack>
            </IPStack.Item>
            {!appContext.isMobileView && (
              <IPStack.Item>
                <TaskTopTasksBar
                  task={props.task}
                  templateTypes={[TaskTypes.Template]}
                  onClickTemplate={props.copyFromTemplate}
                  onClickFinish={onClickFinish}
                  onClickArchive={onClickArchive}
                  onClickReopen={auditFrozen ? undefined : onClickReopen}
                  onClickFollowUp={props.onClickFollowUp}
                  navigateExternal={props.navigateExternal}
                  canUpdate={props.canUpdate}
                  canDelete={props.canDelete}
                  orgTask={props.orgTask}
                  canSave={!hasErrors()}
                  isLoading={props.isLoading}
                  isActionPending={props.isActionPending}
                  onUploadEvidence={props.onUploadEvidence}
                />
              </IPStack.Item>
            )}
            <IPStack.Item>
              <IPIconButton iconProps={cancelIcon} onClick={onCancel} />
            </IPStack.Item>
          </IPStack>
          <IPStack.Item>
            <TaskDetailOwner
              orgTask={props.orgTask}
              task={props.task}
              onUpdate={props.onUpdateTask}
              canUpdate={props.canUpdate}
            />
          </IPStack.Item>
          <IPSeparator />
          <ErrorMessage error={props.error} customAction={props.onAfterErrorAction} />
        </IPStack.Item>
        {appContext.isMobileView && (
          <IPStack.Item>
            <TaskTopTasksBar
              task={props.task}
              templateTypes={[TaskTypes.Template]}
              onClickTemplate={props.copyFromTemplate}
              onClickFinish={onClickFinish}
              onClickArchive={onClickArchive}
              onClickReopen={auditFrozen ? undefined : onClickReopen}
              onClickFollowUp={props.onClickFollowUp}
              navigateExternal={props.navigateExternal}
              canUpdate={props.canUpdate}
              canDelete={props.canDelete}
              orgTask={props.orgTask}
              canSave={!hasErrors()}
              isLoading={props.isLoading}
              isActionPending={props.isActionPending}
              onUploadEvidence={props.onUploadEvidence}
            />
          </IPStack.Item>
        )}
        <IPStack.Item grow>
          <IPStack horizontal styles={globalStackStylesHeight100} tokens={globalStackTokensGapSmall}>
            {!appContext.isMobileView && (
              <Fragment>
                <IPStack.Item styles={{ root: { width: '50%' } }}>{getTaskDetails()}</IPStack.Item>
                <IPSeparator vertical></IPSeparator>
              </Fragment>
            )}
            <IPStack.Item styles={!appContext.isMobileView ? { root: { width: '50%' } } : { root: { width: '100%' } }}>
              <IPStack verticalFill tokens={globalStackTokensGapSmall}>
                <IPStack.Item>
                  <IPPivot
                    selectedKey={selectedPivot}
                    onLinkClick={(item) => setSelectedPivot(item?.props.itemKey ?? '')}
                    overflowBehavior="menu"
                  >
                    {appContext.isMobileView && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-details"
                        key="TaskDetails"
                        itemKey="TaskDetails"
                        headerText={t('task:Pivot.TaskDetails')}
                      />
                    )}
                    {/* The tab exists even when no form is assigned yet: without it the auditor would
                        get no instrument tab at all. Its body is then the empty state. */}
                    {props.hasFormFeature && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-tasktype"
                        key="TaskType"
                        itemKey="TaskType"
                        headerText={
                          hasForm && props.task.taskTypeId
                            ? TaskType.forTask(
                                props.task.taskTypeId,
                                appContext,
                                t,
                                props.task.taskTypeFamilyId,
                                props.task.formVersion,
                              )?.tabName
                            : t('audit:Execute.Task.EmptyForm.Tab')
                        }
                        onRenderItemLink={appContext.isMobileView ? undefined : onRenderTaskTypePivotItem}
                      />
                    )}
                    <IPPivotItem
                      className="isoplanner-task-pivot-context"
                      key="Context"
                      itemKey="Context"
                      headerText={t('task:Pivot.Context')}
                    />
                    <IPPivotItem
                      className="isoplanner-task-pivot-library"
                      key="Resources"
                      itemKey="Resources"
                      headerText={t('task:Pivot.Resources')}
                    />
                    {props.task.commentTrailId > 0 && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-activity"
                        key="Activities"
                        itemKey="Activities"
                        headerText={t('task:Pivot.Activities')}
                      />
                    )}
                    {props.task.auditTrailId > 0 && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-changelog"
                        key="Audit"
                        itemKey="Audit"
                        headerText={t('task:Pivot.Audit')}
                      />
                    )}
                  </IPPivot>
                </IPStack.Item>
                {selectedPivot === 'TaskDetails' && (
                  <Fragment>
                    <IPStack.Item grow style={{ height: appContext.isMobileView ? '40vh' : 0 }}>
                      {getTaskDetails()}
                    </IPStack.Item>
                  </Fragment>
                )}
                <TaskTypeForm
                  isOpen={selectedPivot === 'TaskType'}
                  task={props.task}
                  links={props.links}
                  compact={true}
                  compactTaskView={props.compactView}
                  readonly={frozen}
                  onUpdateData={props.onUpdateKPIData}
                  onSetData={props.onSetKPIData}
                  onUpdateTaskForForm={props.onUpdateTaskForForm}
                  addLinks={props.addLinkToTaskState}
                  setLinks={props.setLinkToTaskState}
                  removeLink={props.removeLinkFromTaskState}
                  showErrors={props.showFormErrors}
                  onValidate={props.onValidateForm}
                  formMode={props.formMode}
                  onExitFormMode={() => props.setFormMode(false)}
                  onClickFinish={onClickFinish}
                  renderEmptyState={renderEmptyFormState}
                  evidenceTasksByCriterionKey={evidenceTasksByCriterionKey}
                  onOpenEvidenceTask={(taskId) => void openEvidenceTask(taskId)}
                  entityNamesByKey={entityNamesByKey}
                  takeOverAiAnswers={takeOverAiAnswers}
                ></TaskTypeForm>
                {/* TaskTypeForm renders nothing at all without a form type, so the second insertion
                    point of the empty state is here: the same prompt, one level up. */}
                {selectedPivot === 'TaskType' && props.hasFormFeature && !hasForm && (
                  <IPStack.Item grow styles={{ root: { paddingTop: 20 } }}>
                    {renderEmptyFormState()}
                  </IPStack.Item>
                )}
                {selectedPivot === 'Resources' && (
                  <TaskLinkList
                    taskLinks={props.links}
                    task={props.task}
                    addLinks={props.addLinkToTaskState}
                    setLinks={props.setLinkToTaskState}
                    removeLink={props.removeLinkFromTaskState}
                    navigateExternal={props.navigateExternal}
                    readonly={frozen}
                  />
                )}
                {selectedPivot === 'Activities' && (
                  <IPStack.Item grow>
                    <ActivityTrail
                      auditTrailId={props.task.auditTrailId}
                      commentTrailId={props.task.commentTrailId}
                      taskId={props.task.taskId}
                      authSchemaIds={[props.task.authSchemaId]}
                      onError={props.onError}
                      readOnly={frozen}
                    />
                  </IPStack.Item>
                )}
                {selectedPivot === 'Audit' && (
                  <IPStack.Item grow>
                    <AuditTrail auditTrailId={props.task.auditTrailId} />
                  </IPStack.Item>
                )}
                {selectedPivot === 'Context' && (
                  <IPStack.Item grow>
                    <TaskContext
                      orgTask={props.orgTask}
                      task={props.task}
                      updateTaskForForm={props.onUpdateTaskForForm}
                      updateTask={props.onUpdateTask}
                      onClickFollowUp={props.onClickFollowUp}
                      onLinkFollowUpToTemplate={props.onLinkFollowUpToTemplate}
                      onCreateEmptyFollowUpTask={props.onCreateEmptyFollowUpTask}
                      allowEdit={
                        !frozen &&
                        canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.Context, appContext)
                      }
                      windowLevel={props.windowLevel}
                      navigateExternal={true}
                    />
                  </IPStack.Item>
                )}
              </IPStack>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
        <IPStack.Item>
          <IPSeparator styles={{ root: { height: 1 } }} />
        </IPStack.Item>
        <IPStack.Item>{footer}</IPStack.Item>
      </IPStack>
      <DialogYesNo
        onNo={() => {
          setShowCancelDialog(false);
        }}
        onYes={() => {
          setShowCancelDialog(false);

          props.onCancel();
        }}
        title={t('task:Cancel.DialogTitle')}
        subText={t('task:Cancel.DialogSubTitle')}
        hidden={!showCancelDialog}
      />
      <DialogOk
        onOk={() => {
          setShowResultRequiredDialog(false);
        }}
        title={t('audit:Execute.Task.ResultRequired.Title')}
        subText={getResultRequiredText()}
        hidden={!showResultRequiredDialog}
      />
      {evidenceTask && (
        //the bound evidence task opens on top of this one: the auditor reads it and comes back
        <SingleTask
          isOpen={true}
          task={evidenceTask}
          windowLevel={(props.windowLevel ?? 0) + 1}
          close={() => setEvidenceTask(undefined)}
          onSave={() => setEvidenceTask(undefined)}
          onRemove={() => setEvidenceTask(undefined)}
          onUpdate={(task) => setEvidenceTask(task)}
          navigateExternal={props.navigateExternal}
        />
      )}
    </IPModal>
  );
};

export default AuditExecutionTask;
